<script setup>
import { useQuality } from '@/composables/useQuality'
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'
import { useRoute, useRouter } from 'vue-router'

// Module 17 (§9.4) — blueprint's own Vue Page list cuma menyebut
// index.vue + [id]/index.vue untuk CAPA (BEDA dengan Incident yang punya
// create.vue terpisah) — form "Buat CAPA" jadi dialog di halaman ini,
// bukan route terpisah, sama pola master-data.
definePage({
  meta: {
    layout: 'default',
    action: 'view',
    subject: 'Capa',
  },
})

const route = useRoute('quality-capa')
const router = useRouter()
const ability = useAbility()
const { capas, loading, saving, fetchCapas, createCapa } = useQuality()

const status = ref('')

const load = () => fetchCapas(status.value ? { status: status.value } : {})

onMounted(() => {
  load()
  if (route.query.incident_id || route.query.title) openCreate()
})
watch(status, load)

const dialog = ref(false)
const form = ref({ incident_id: null, title: '', description: '', root_cause: '', pic_id: null, due_date: '' })
const errors = ref({})

// PIC autocomplete — pola sama persis components/shared/TaskList.vue
// (butuh user.view, bukan Employee picker — lihat CLAUDE.md § Task Engine).
const picOptions = ref([])
const picSearchLoading = ref(false)

async function searchPic(query) {
  picSearchLoading.value = true
  try {
    const response = await useApi('/api/v1/users', { query: { search: query, per_page: 20 } }).get()
    const data = response?.data ?? response
    const rows = Array.isArray(data) ? data : (data?.data ?? [])

    picOptions.value = rows.map(u => ({ title: u.name, value: u.id }))
  } catch (e) {
    picOptions.value = []
  } finally {
    picSearchLoading.value = false
  }
}

// ?title=/?description= dipakai components/Ai/DraftPreviewCard.vue (Module
// 22, §29.1) supaya "AI Draft CAPA" mengisi form normal ini — user tetap
// harus klik Simpan, AI tidak pernah menulis CAPA langsung (BR-012).
const openCreate = () => {
  form.value = {
    incident_id: route.query.incident_id ? Number(route.query.incident_id) : null,
    title: route.query.title ? String(route.query.title) : '',
    description: route.query.description ? String(route.query.description) : '',
    root_cause: '', pic_id: null, due_date: '',
  }
  errors.value = {}
  dialog.value = true
}

const save = async () => {
  errors.value = {}
  try {
    const capa = await createCapa(form.value)

    dialog.value = false
    router.push({ name: 'quality-capa-id', params: { id: capa.id } })
  } catch (e) {
    if (e?.status === 422) errors.value = e?.data?.errors ?? {}
    else console.error('Create CAPA error:', e)
  }
}

const statusOptions = [
  { title: 'Semua', value: '' },
  { title: 'Draft', value: 'draft' },
  { title: 'Berjalan', value: 'open' },
  { title: 'Selesai', value: 'closed' },
]

const statusColor = value => ({ draft: 'secondary', open: 'info', closed: 'success' }[value] ?? 'secondary')

const headers = [
  { title: 'Judul', key: 'title' },
  { title: 'PIC', key: 'pic' },
  { title: 'Tenggat', key: 'due_date', align: 'center' },
  { title: 'Status', key: 'status', align: 'center' },
]
</script>

<template>
  <div>
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap gap-3">
          <div>
            <h4 class="text-h4 font-weight-bold">
              CAPA
            </h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Corrective and Preventive Action (§9.4, BR-010)
            </p>
          </div>
          <VBtn
            v-if="ability.can('create', 'Capa')"
            prepend-icon="ri-add-line"
            @click="openCreate"
          >
            Buat CAPA
          </VBtn>
        </div>
      </VCol>
    </VRow>

    <VCard class="mb-4">
      <VCardText>
        <VSelect
          v-model="status"
          :items="statusOptions"
          label="Status"
          density="compact"
          style="max-width: 260px;"
        />
      </VCardText>
    </VCard>

    <VCard>
      <VDataTable
        :headers="headers"
        :items="capas"
        :loading="loading"
        hide-default-footer
        @click:row="(e, { item }) => router.push({ name: 'quality-capa-id', params: { id: item.id } })"
      >
        <template #item.pic="{ item }">
          {{ item.pic?.name ?? '-' }}
        </template>
        <template #item.status="{ item }">
          <VChip
            size="small"
            :color="statusColor(item.status)"
            variant="tonal"
          >
            {{ item.status }}
          </VChip>
        </template>
        <template #no-data>
          <div class="text-center py-10">
            <p class="text-medium-emphasis">
              Belum ada CAPA
            </p>
          </div>
        </template>
      </VDataTable>
    </VCard>

    <VDialog
      v-model="dialog"
      max-width="640"
      persistent
    >
      <VCard>
        <VCardTitle class="pa-6 pb-0">
          Buat CAPA
        </VCardTitle>
        <VCardText class="pa-6">
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="form.title"
                label="Judul *"
                :error-messages="errors.title"
              />
            </VCol>
            <VCol cols="12">
              <VTextarea
                v-model="form.description"
                label="Deskripsi *"
                rows="3"
                :error-messages="errors.description"
              />
            </VCol>
            <VCol cols="12">
              <VTextarea
                v-model="form.root_cause"
                label="Akar Masalah (opsional)"
                rows="2"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VAutocomplete
                v-model="form.pic_id"
                :items="picOptions"
                :loading="picSearchLoading"
                label="PIC (opsional)"
                clearable
                @update:search="searchPic"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="form.due_date"
                type="date"
                label="Tenggat (opsional)"
              />
            </VCol>
          </VRow>
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="saving"
            @click="dialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            @click="save"
          >
            Simpan
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
